import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorCode, errorMessage } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { BorderedRichTextEditor } from '@/messages/BorderedRichTextEditor';
import { useRichTextEditor } from '~/hooks/useRichTextEditor';
import { FormContainer, GroupHeaderWrapper } from '~/styles/layoutElements';
import { MASS_MESSAGING_SENT_FLAG } from './$groupName.mass-messaging.index';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/mass-messaging/new',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sendError, setSendError] = useState<unknown>(null);
  const [showEmptyMessageError, setShowEmptyMessageError] = useState(false);
  const { groupName } = useParams({
    from: '/_app/_dashboard/groups/$groupName/mass-messaging/new',
  });

  const { data: group } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = group?.id ?? 0;

  const { mutateAsync: sendMassMessage, isPending } =
    api.sendMassMessage.useMutation();
  const sendErrorMessage = useMassMessageErrorMessage(sendError);

  const editor = useRichTextEditor();
  const { editorRef, getEditorContent, getEditorHtml } = editor;

  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount to set initial editor content
  useEffect(() => {
    // Legacy's Repost prefills the composer with the stored body; the list
    // passes it through sessionStorage rather than the URL.
    //
    // This is an `innerHTML` write, which is safe because the server runs
    // `MassMessageSummary.content` through the announcement allow-list on read.
    // Migrated rows are stored unsanitised, so that read-side pass must stay.
    const repostedMessage = sessionStorage.getItem('repostedMessage');
    if (repostedMessage && editorRef.current) {
      editorRef.current.innerHTML = repostedMessage;
      sessionStorage.removeItem('repostedMessage');
    }
  }, []);

  const handleBack = () => {
    navigate({
      to: '/groups/$groupName/mass-messaging/',
      params: { groupName },
    });
  };

  const handleSend = async (): Promise<void> => {
    // The plain-text form, which is what an emptiness test has to look at: a
    // contenteditable that has only been focused holds `<div><br></div>`.
    if (!getEditorContent()) {
      setShowEmptyMessageError(true);
      setSendError(null);
      return;
    }
    if (groupId === 0) return;

    setShowEmptyMessageError(false);
    setSendError(null);
    try {
      await sendMassMessage({
        path: { group_id: groupId },
        // The markup: `getEditorContent()` is `innerText.trim()`, which drops
        // formatting and line structure. The group announcement makes the same
        // choice.
        body: { content: getEditorHtml() },
      });
    } catch (failed) {
      setSendError(failed);
      return;
    }
    await api.listMassMessages.invalidateQueries();
    sessionStorage.setItem(MASS_MESSAGING_SENT_FLAG, '1');
    navigate({
      to: '/groups/$groupName/mass-messaging/',
      params: { groupName },
    });
  };

  return (
    <>
      {showEmptyMessageError && (
        <div style={{ marginTop: '16px' }}>
          <AlertBox variant="error">
            {t('wbs.please-enter-message-content')}
          </AlertBox>
        </div>
      )}
      {sendErrorMessage !== null && (
        <div style={{ marginTop: '16px' }}>
          <AlertBox variant="error">{sendErrorMessage}</AlertBox>
        </div>
      )}
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('wbs.compose-a-new-message')}
          placement="mass_messaging"
        />
      </GroupHeaderWrapper>
      <FormContainer>
        <LargeArrowBackButton onClick={handleBack} />
        <Grid>
          <SectionLabel>{t('wbs.recipients')}</SectionLabel>
          {/* One static sentence and no recipient control: for the group
             types that have this tab, legacy's composer shows only this
             sentence. */}
          <RecipientsText>{t('wbs.message-will-be-sent')}</RecipientsText>

          <SectionLabel>{t('wbs.message')}</SectionLabel>
          <EditorSection>
            <BorderedRichTextEditor
              editor={editor}
              visibleTools={['bold', 'italic', 'underline', 'blockquote']}
              maxHeight="400px"
              placeholder=""
              onSubmit={() => {
                void handleSend();
              }}
            />
            <SendRow>
              <WBSButton
                wide
                type="button"
                // The send is id-addressed and the slug resolves a render
                // later; without this the button is live over a no-op.
                disabled={groupId === 0}
                isLoading={isPending}
                onClick={() => {
                  void handleSend();
                }}
              >
                {t('wbs.send')}
              </WBSButton>
            </SendRow>
          </EditorSection>
        </Grid>
      </FormContainer>
    </>
  );
}

/**
 * The server's three refusals (empty, over 1500 characters, nobody to send to),
 * in legacy's words. Anything else gets the generic string.
 */
function useMassMessageErrorMessage(error: unknown): string | null {
  const { t } = useTranslation();

  if (error === null) return null;

  switch (errorCode(error)) {
    case 'mass_message_content_required':
      return t('wbs.please-enter-message-content');
    case 'mass_message_too_long':
      return t('wbs.you-have-exceeded-the');
    case 'mass_message_no_recipients':
      return t('wbs.no-recipients-found');
    default:
      return errorMessage(error, t('wbs.something-went-wrong-try'));
  }
}

const Grid = styled.div`
  align-items: start;
  display: grid;
  gap: 28px 30px;
  grid-template-columns: auto 1fr;
`;

const SectionLabel = styled.span`
  color: var(--black);
  font-size: 13px;
  font-weight: 600;
  padding-top: 6px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const RecipientsText = styled.p`
  color: var(--black);
  font-size: 13px;
  margin: 0;
  padding-top: 4px;
`;

const EditorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SendRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;
